import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { LoadingOverlayService } from 'src/app/shared/loading-overlay/loading-overlay.service';
import {
    shouldShowExitConfirmationModal,
    checkIfModeExistsInQueryParams
} from 'src/app/shared/utility/campaign';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { CampaignHelperService } from '../../../services/campaign-helper.service';
import { CampaignService } from '../../../services/campaign.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { CanComponentDeactivate } from '../guards/can-deactivate.interface';
import { CampaignStatus } from 'src/app/models/campaign';

@Component({
    selector: 'app-add-title',
    templateUrl: './add-title.component.html',
    styleUrls: ['./add-title.component.scss']
})
export class AddTitleComponent implements OnInit, CanComponentDeactivate {
    public form: FormGroup;
    public industries: any = [];
    public filteredIndustries: any[] = [];

    // Drives the repeated text-input blocks in the template via *ngFor.
    readonly topFields = [
        { name: 'campaign_name', type: 'text', placeholder: 'Campaign Name', cols: 'col-12 col-lg-6 mb-4' },
        { name: 'campaign_brand', type: 'text', placeholder: 'Campaign Brand', cols: 'col-12 col-lg-6 mb-4' },
        { name: 'campaign_budget', type: 'number', placeholder: 'Campaign Budget (Excluding VAT and Municipality fees)', cols: 'col-12 col-lg-6 mb-4' }
    ];
    readonly issuerFields = [
        { name: 'issuer_name', type: 'text', placeholder: 'Enter issuer name', cols: 'col-12 col-lg-6 mb-4 flex-grow-1' },
        { name: 'issuer_email', type: 'text', placeholder: "Issuer's email", cols: 'col-12 col-lg-6 mb-4 flex-grow-1' }
    ];

    loading = false;
    campaignId;
    campaign;
    previewMode = false;
    partialEditMode = false;
    liveEditMode = false;
    /** Terms & Conditions / Privacy Policy must be accepted every time a client creates a campaign. */
    showTermsAcceptance = false;

    public constructor(
        private LoadingOverlayService: LoadingOverlayService,
        private formBuilder: FormBuilder,
        private campaignService: CampaignService,
        private router: Router,
        private campaignHelperService: CampaignHelperService,
        private alertService: AlertService,
        private route: ActivatedRoute,
        private authenticationService: AuthenticationService
    ) { }

    get f() {
        return this.form.controls;
    }

    ngOnInit(): void {
        this.previewMode = checkIfModeExistsInQueryParams(this.route, 'preview');
        this.partialEditMode = checkIfModeExistsInQueryParams(this.route, 'partial-edit-mode');
        this.liveEditMode = checkIfModeExistsInQueryParams(this.route, 'live-edit-mode');
        this.showTermsAcceptance = this.isNewCampaignFlow();
        this.getIndustries().then(() => {
            this.getCampaign();
        });

        this.initForm();
    }

    canDeactivate(): boolean {
        return this.previewMode;
    }

    private isReadOnlyFlow(): boolean {
        return this.previewMode || !this.authenticationService.isClient();
    }

    /**
     * True while a client is planning a campaign that has not been published yet — a brand-new one
     * or a draft they have come back to.
     *
     * Deliberately not keyed on the route id: the draft gains one the moment Step 1 is saved, so
     * keying on it made the checkbox vanish on the way back from Step 2 and on reopening a draft,
     * taking its control (and therefore its validation) with it.
     */
    private isNewCampaignFlow(): boolean {
        return (
            !this.previewMode &&
            !this.partialEditMode &&
            !this.liveEditMode &&
            this.authenticationService.isClient()
        );
    }

    async saveDraft(): Promise<boolean> {
        if (this.isReadOnlyFlow()) {
            return true;
        }

        this.alertService.clear();
        if (this.form.invalid) {
            this.alertService.error('Please fill in all required fields before saving.', {
                id: 'save-campaign',
                autoClose: true
            });
            return false;
        }

        return new Promise(resolve => {
            this.LoadingOverlayService.show();
            this.persistCampaign().subscribe({
                next: data => {
                    this.LoadingOverlayService.hide();
                    this.campaign = data;
                    this.campaignId = data._id;
                    this.alertService.success('Campaign saved successfully.', {
                        id: 'save-campaign',
                        autoClose: true
                    });
                    resolve(true);
                },
                error: error => {
                    this.showSaveError(error);
                    this.LoadingOverlayService.hide();
                    resolve(false);
                }
            });
        });
    }

    /** The Step 1 "information" fields, used as the slim update payload / create args. */
    private informationPayload() {
        return {
            title: this.f.campaign_name.value,
            brand: this.f.campaign_brand.value,
            budget: this.f.campaign_budget.value,
            billingMethod: this.f.campaign_billing_method.value,
            industryId: this.f.industry.value ? this.f.industry.value : null,
            issuerName: this.f.issuer_name.value,
            issuerEmail: this.f.issuer_email.value,
            // Only present on the create path; the server stamps the timestamp and version itself.
            termsAccepted: this.showTermsAcceptance ? this.f.terms_accepted.value === true : undefined
        };
    }

    /** Updates the existing campaign via the slim information endpoint, or creates a new one. */
    private persistCampaign(): Observable<any> {
        const info = this.informationPayload();
        if (this.campaign) {
            return this.campaignService.updateInformation(this.campaign._id, info);
        }
        return this.campaignService.create(
            info.title,
            info.brand,
            info.budget,
            info.billingMethod,
            info.industryId,
            info.issuerName,
            info.issuerEmail,
            info.termsAccepted === true
        );
    }

    private showSaveError(error: any): void {
        this.alertService.error('Error: ' + error, { id: 'save-campaign', autoClose: true });
    }

    private goToStep2(id: string): void {
        this.router.navigateByUrl(
            '/campaign/step-2/' + id + (this.partialEditMode ? '?partial-edit-mode=true' : '')
        );
    }

    hasError(controlName: string): boolean {
        const control = this.form.get(controlName);
        return !!(control && control.errors && control.touched);
    }

    getCampaign() {
        this.campaignId = this.route.snapshot.paramMap.get('id');
        if (this.campaignId != null && this.campaignId != '') {
            this.campaignService.getById(this.campaignId).subscribe(campaign => {
                this.campaign = campaign;
                this.f.campaign_name.patchValue(campaign.title);
                this.f.campaign_brand.patchValue(campaign.brand);
                this.f.campaign_budget.patchValue(campaign.budget);
                this.f.campaign_billing_method.patchValue(campaign.billingMethod);
                this.f.industry.patchValue(campaign.industryId);
                if (this.campaign && this.campaign.wasPublished) {
                    this.form.get('industry').disable();
                } else {
                    this.form.get('industry').enable();
                }
                this.f.issuer_name.patchValue(campaign.issuerName);
                this.f.issuer_email.patchValue(campaign.issuerEmail);
                // The server stamps termsAcceptedAt when the campaign is created, so a draft the
                // client returns to shows its acceptance already ticked rather than asking again.
                if (this.f.terms_accepted && campaign.termsAcceptedAt) {
                    this.f.terms_accepted.patchValue(true);
                }
                // Published campaigns are no longer being planned; the acceptance is a record by then.
                if (this.campaign && this.campaign.wasPublished) {
                    this.showTermsAcceptance = false;
                    this.form.removeControl('terms_accepted');
                }
                this.disableFieldsForPreview();
            });
        } else {
            this.campaign = null;
        }
    }

    disableFieldsForPreview() {
        const controls = [
            'industry',
            'campaign_name',
            'campaign_brand',
            'campaign_budget',
            'campaign_billing_method',
            'issuer_name',
            'issuer_email'
        ];
        if (this.previewMode || this.partialEditMode || this.liveEditMode) {
            controls.forEach(c => this.form.get(c).disable());
            return;
        }
        controls.forEach(c => this.form.get(c).enable());
        // Billing method is editable by admins only.
        if (!this.authenticationService.hasAdminRole()) {
            this.form.get('campaign_billing_method').disable();
        }
    }

    isBeingEditedMode() {
        return this.campaign.status == CampaignStatus.BEING_EDITED;
    }

    private getIndustries(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.campaignHelperService
                .getIndustries()
                .pipe()
                .subscribe({
                    next: (data: any[]) => {
                        this.industries = data;
                        this.filteredIndustries = data;
                        resolve();
                    },
                    error: error => {
                        reject(error);
                    }
                });
        });
    }

    displayIndustryName = (industryId: number): string => {
        const industry = this.industries.find(ind => ind.idIndustry === industryId);
        return industry ? industry.name : '';
    };

    onIndustrySelected(industryId: number) {
        this.form.get('industry')?.setValue(industryId);
    }

    filterIndustries(value: string | number) {
        let filterValue = '';

        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else {
            const industry = this.industries.find(i => i.idIndustry === value);
            filterValue = industry ? industry.name.toLowerCase() : '';
        }

        this.filteredIndustries = this.industries.filter(industry =>
            industry.name.toLowerCase().includes(filterValue)
        );
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            campaign_name: new FormControl(
                this.campaign ? this.campaign.title : null,
                RxwebValidators.required({ message: 'Campaign name is required.' })
            ),
            campaign_brand: new FormControl(
                this.campaign ? this.campaign.brand : null,
                RxwebValidators.required({ message: 'Campaign brand is required.' })
            ),
            campaign_billing_method: new FormControl(
                this.campaign ? this.campaign.billingMethod : null,
                RxwebValidators.required({ message: 'Billing method is required.' })
            ),
            campaign_budget: new FormControl(this.campaign ? this.campaign.budget : null, [
                RxwebValidators.required({
                    message: 'Campaign Budget is required and should be a number.'
                }),
                RxwebValidators.minNumber({
                    value: 1,
                    message: 'Budget should be a positive number. '
                })
            ]),
            issuer_name: new FormControl(this.campaign ? this.campaign.issuerName : null, [
                RxwebValidators.required({ message: 'Issuer Name is required.' }),
                RxwebValidators.minLength({
                    value: 3,
                    message: 'Issuer Name must be at least 3 characters.'
                }),
                RxwebValidators.pattern({
                    expression: { pattern: /^[A-Za-z ]+$/ },
                    message: 'Issuer Name must only contain letters.'
                })
            ]),
            issuer_email: new FormControl(this.campaign ? this.campaign.issuerEmail : null, [
                RxwebValidators.required({ message: 'Issuer email is required.' }),
                RxwebValidators.email({ message: 'Please enter a valid email address' })
            ]),
            industry: new FormControl(this.campaign ? { value: this.campaign.industryId } : null)
        });

        if (this.showTermsAcceptance) {
            this.form.addControl(
                'terms_accepted',
                new FormControl(
                    false,
                    RxwebValidators.requiredTrue({
                        message:
                            'Please agree to our Terms & Conditions and our Privacy Policy before planning your campaign.'
                    })
                )
            );
        }

        this.form.get('industry').valueChanges.subscribe(value => {
            this.filterIndustries(value);
        });
    }

    createCampaign() {
        if (this.isReadOnlyFlow()) {
            this.goToStep2(this.campaign._id);
            return;
        }

        this.alertService.clear();
        if (this.form.invalid) {
            return;
        }
        this.LoadingOverlayService.show();
        this.persistCampaign().subscribe({
            next: data => {
                this.LoadingOverlayService.hide();
                this.goToStep2(data._id);
            },
            error: error => {
                this.showSaveError(error);
                this.LoadingOverlayService.hide();
            }
        });
    }
}
